/**
 * ImageModal component for displaying images in a popup window.
 * Features a responsive design with the application's theme colors.
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import LoadingSpinner from "./LoadingSpinner";

/**
 * ImageModal component that displays an image in a modal dialog.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is currently open
 * @param {Function} props.onClose - Callback function called when modal is closed
 * @param {string} props.imageData - Base64 encoded image data
 * @param {string} props.guid - The GUID of the image being displayed
 * @param {string} props.timestamp - Timestamp from the API response (p attribute)
 * @param {string} props.buildingName - The name of the building
 * @param {boolean} props.isLoading - Whether the image is currently loading
 * @returns {JSX.Element} The rendered ImageModal component
 */
const ImageModal = ({ isOpen, onClose, imageData, guid, timestamp, buildingName, isLoading = false }) => {
  const { t } = useTranslation();
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [imageUrl, setImageUrl] = useState(null);
  const [rotation, setRotation] = useState(0);
  const imgRef = useRef(null);
  const blobUrlRef = useRef(null);

  // Convert Base64 to Blob URL for better performance
  useEffect(() => {
    if (isOpen && imageData) {
      setImageError(false);
      setImageLoading(true);
      setLoadProgress(0);
      setRotation(0); // Reset rotation when new image loads
      
      // Clean up previous blob URL
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }

      // Optimized Base64 to Blob conversion
      const convertImage = () => {
        try {
          // More efficient Base64 to binary conversion
          const binaryString = atob(imageData);
          const bytes = new Uint8Array(binaryString.length);
          
          // Use typed array for faster conversion
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          
          const blob = new Blob([bytes], { type: 'image/jpeg' });
          const url = URL.createObjectURL(blob);
          blobUrlRef.current = url;
          setImageUrl(url);
          
          // Update progress
          setLoadProgress(90);
        } catch {
          // Fallback to data URL if conversion fails
          setImageUrl(`data:image/jpeg;base64,${imageData}`);
          setLoadProgress(90);
        }
      };

      // Simulate progress while converting
      const progressInterval = setInterval(() => {
        setLoadProgress(prev => {
          if (prev >= 85) {
            clearInterval(progressInterval);
            return 85;
          }
          return prev + Math.random() * 10;
        });
      }, 100);
      
      // Use requestIdleCallback if available, otherwise setTimeout
      let timeoutId;
      let idleCallbackId;
      
      if (window.requestIdleCallback) {
        idleCallbackId = requestIdleCallback(convertImage, { timeout: 1000 });
        timeoutId = setTimeout(() => {
          if (idleCallbackId) {
            cancelIdleCallback(idleCallbackId);
          }
          convertImage();
        }, 1000);
      } else {
        // Fallback to setTimeout
        timeoutId = setTimeout(convertImage, 0);
      }
      
      // Return cleanup function
      return () => {
        clearInterval(progressInterval);
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (idleCallbackId && window.cancelIdleCallback) {
          cancelIdleCallback(idleCallbackId);
        }
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = null;
        }
      };
    } else if (!isOpen) {
      // Clean up blob URL when modal closes
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setImageUrl(null);
    }
  }, [isOpen, imageData]);

  // Convert timestamp from UTC to Finland timezone
  const formattedTimestamp = useMemo(() => {
    if (!timestamp || typeof timestamp !== 'string') {
      return null;
    }
    
    try {
      // Parse the timestamp string (format: "11/6/2025 9:15:22 AM +00:00")
      const date = new Date(timestamp);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return timestamp; // Return original if parsing fails
      }
      
      // Convert to Finland timezone (Europe/Helsinki)
      // This automatically handles DST (EET/EEST)
      const finlandTime = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Europe/Helsinki',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(date);
      
      return finlandTime || timestamp;
    } catch {
      // If conversion fails, return original timestamp
      return timestamp;
    }
  }, [timestamp]);

  if (!isOpen) return null;

  const handleImageLoad = () => {
    setImageLoading(false);
    setLoadProgress(100);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoading(false);
    setLoadProgress(0);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleRotate = () => {
    setRotation((prevRotation) => (prevRotation + 90) % 360);
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="bg-white rounded-xl shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden transform transition-all duration-300">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-[#cabb78] to-[#b5a666] px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-white">
              {t("image_viewer")}
            </h2>
            {buildingName && (
              <span className="text-lg text-white opacity-90">
                - {buildingName}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors duration-200 p-1"
            aria-label={t("close")}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col items-center justify-center min-h-[400px]">
          {(isLoading || imageLoading) && (
            <div className="flex flex-col items-center justify-center space-y-4">
              <LoadingSpinner />
              <p className="text-gray-600">{t("loading_image")}</p>
              
              {/* Progress Bar */}
              <div className="w-64 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-[#cabb78] to-[#b5a666] h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${loadProgress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-500">{Math.round(loadProgress)}%</p>
            </div>
          )}
          
          {imageError ? (
            <div className="flex flex-col items-center justify-center space-y-4 text-center">
              <div className="text-red-500">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">{t("image_load_error")}</h3>
              <p className="text-sm text-gray-500">{t("image_load_error_description")}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-gradient-to-r from-[#cabb78] to-[#b5a666] text-white rounded-lg hover:from-[#b5a666] hover:to-[#cabb78] transition-all duration-300"
              >
                {t("retry")}
              </button>
            </div>
          ) : (
            imageUrl && !isLoading && (
              <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
                <img
                  ref={imgRef}
                  src={imageUrl}
                  alt={`Image for GUID: ${guid}`}
                  className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-lg transition-all duration-300"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  style={{ 
                    opacity: imageLoading ? 0 : 1,
                    display: imageLoading ? 'none' : 'block',
                    transform: `rotate(${rotation}deg)`,
                    transformOrigin: 'center center'
                  }}
                  loading="eager"
                  decoding="async"
                />
              </div>
            )
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
          <div className="flex flex-col space-y-1">
            {formattedTimestamp && (
              <div className="text-sm text-gray-600 font-medium">
                {t("timestamp")}: {formattedTimestamp}
              </div>
            )}
          </div>
          <div className="flex space-x-4">
            {imageUrl && !isLoading && !imageError && (
              <button
                onClick={handleRotate}
                className="px-4 py-2 bg-gradient-to-r from-[#cabb78] to-[#b5a666] text-white rounded-lg 
                         hover:from-[#b5a666] hover:to-[#cabb78] transition-all duration-300 
                         transform hover:scale-105 shadow-md hover:shadow-lg font-medium
                         inline-flex items-center gap-2"
                aria-label={t("rotate_image")}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {t("rotate")}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gradient-to-r from-[#cabb78] to-[#b5a666] text-white rounded-lg 
                       hover:from-[#b5a666] hover:to-[#cabb78] transition-all duration-300 
                       transform hover:scale-105 shadow-md hover:shadow-lg font-medium"
            >
              {t("close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

ImageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  imageData: PropTypes.string,
  guid: PropTypes.string,
  timestamp: PropTypes.string,
  buildingName: PropTypes.string,
  isLoading: PropTypes.bool,
};

export default ImageModal;

